import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { V2, pickLang } from "../copy";
import { Display, Eyebrow } from "./Primitives";

const CARDS = ["lifespan", "environmental", "time"] as const;
const STATS = ["lifespan", "water", "waste", "satisfaction"] as const;

/** Impact as an editorial spread: sticky photo left, argument right. */
const ImpactV2: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const c = V2[pickLang(currentLanguage)].impact;

  return (
    <section className="border-t border-ink/10 bg-bone py-24 text-ink sm:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-12 px-4 sm:px-6 lg:grid-cols-12">
        <figure className="v2-reveal lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
          <div className="overflow-hidden rounded-[6px]">
            <img
              src="/oldshooes.webp"
              alt={t("impact.cards.lifespan.title")}
              loading="lazy"
              className="aspect-[4/5] w-full object-cover grayscale transition-all duration-1000 hover:grayscale-0"
            />
          </div>
          <figcaption className="mt-3 flex items-start gap-3 font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-ink/50">
            <span className="mt-[7px] h-px w-6 flex-shrink-0 bg-teal" />
            {c.caption}
          </figcaption>
        </figure>

        <div className="lg:col-span-7">
          <Eyebrow index="07">{c.label}</Eyebrow>
          <Display className="mt-5 text-[clamp(2.4rem,6vw,5.5rem)]">
            {t("impact.title")}
          </Display>
          <p className="v2-reveal mt-6 max-w-xl text-lg text-ink/65" data-delay="2">
            {t("impact.subtitle")}
          </p>

          <div className="mt-12">
            {CARDS.map((k, i) => (
              <div
                key={k}
                className="v2-reveal grid gap-3 border-t border-ink/15 py-7 sm:grid-cols-12 sm:gap-8"
                data-delay={String(i + 1)}
              >
                <div className="font-mono text-[11px] tracking-[0.22em] text-ink/40 sm:col-span-1">
                  {`0${i + 1}`}
                </div>
                <h3 className="font-display text-2xl leading-tight sm:col-span-4 sm:text-3xl">
                  {t(`impact.cards.${k}.title`)}
                </h3>
                <p className="text-sm leading-relaxed text-ink/60 sm:col-span-7">
                  {t(`impact.cards.${k}.description`)}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-4">
            {STATS.map((k, i) => (
              <div
                key={k}
                className="v2-reveal bg-bone p-5 sm:p-6"
                data-delay={String(i + 1)}
              >
                <div className="font-display text-4xl leading-none sm:text-5xl">
                  {t(`impact.stats.${k}.value`)}
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase leading-snug tracking-[0.18em] text-ink/50">
                  {t(`impact.stats.${k}.label`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactV2;
